import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  loadSurveyStats,
  loadSurveyTimeseries,
  setActiveBucket,
  setSurveyDateRange,
} from "../../redux/slices/belediyeSurveyReportsSlice";
import { downloadSurveyReportCsv } from "../../services/belediyeApi";

// Başvurular ile AYNI başlık/araç-çubuğu mimarisi + paylaşılan bileşenler
import "../ApplicationsPage.css";
import "../ApplicationsPage.mobile.css";
import "./SurveyReportDetailPage.css";

import PageHeader from "../../components/utils/PageHeader";
import FilterSheet from "../../components/utils/FilterSheet";

import {
  ArrowDownTrayIcon,
  ClockIcon,
  ArrowsUpDownIcon,
} from "@heroicons/react/24/outline";

const errToText = (err) => {
  if (!err) return null;
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const msg = err.message || "Bir hata oluştu.";
    const status = err.status ? ` (HTTP ${err.status})` : "";
    return `${msg}${status}`;
  }
  return String(err);
};

const ymd = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

export default function SurveyReportDetailPage() {
  const { surveyId } = useParams();
  const dispatch = useDispatch();

  const {
    stats,
    statsLoading,
    statsError,
    timeseriesLoading,
    timeseriesError,
    timeseriesByBucket,
    lastQuery,
  } = useSelector((s) => s.belediyeSurveyReports);

  // Tarih filtresi CANLI (Başvurular gibi): değişince doğrudan uygulanır.
  const [appliedFrom, setAppliedFrom] = useState(lastQuery?.date_from || "");
  const [appliedTo, setAppliedTo] = useState(lastQuery?.date_to || "");

  const [bucket, setBucket] = useState("month");
  const [seriesOrder, setSeriesOrder] = useState("asc"); // asc: eski->yeni, desc: yeni->eski

  const statsErrorText = useMemo(() => errToText(statsError), [statsError]);
  const timeseriesErrorText = useMemo(() => errToText(timeseriesError), [timeseriesError]);

  useEffect(() => {
    dispatch(setActiveBucket(bucket));
  }, [dispatch, bucket]);

  useEffect(() => {
    if (!surveyId) return;

    dispatch(
      loadSurveyStats({
        surveyId,
        date_from: appliedFrom || null,
        date_to: appliedTo || null,
      })
    );

    dispatch(
      loadSurveyTimeseries({
        surveyId,
        date_from: appliedFrom || null,
        date_to: appliedTo || null,
        period: bucket,
      })
    );

    dispatch(
      setSurveyDateRange({
        date_from: appliedFrom || null,
        date_to: appliedTo || null,
      })
    );
  }, [dispatch, surveyId, appliedFrom, appliedTo, bucket]);

  const activeTimeseries = useMemo(() => {
    return timeseriesByBucket?.[bucket] ?? null;
  }, [timeseriesByBucket, bucket]);

  const displaySeries = useMemo(() => {
    const arr = Array.isArray(activeTimeseries?.series) ? activeTimeseries.series : [];
    return seriesOrder === "desc" ? [...arr].reverse() : arr;
  }, [activeTimeseries, seriesOrder]);

  const maxSeriesResponses = useMemo(() => {
    let max = 1;
    for (const r of displaySeries) max = Math.max(max, Number(r?.responses || 0));
    return max;
  }, [displaySeries]);

  const title = useMemo(() => {
    if (!stats?.survey?.name) return "Anket Raporu";
    return `${stats.survey.name} (v${stats.survey.version})`;
  }, [stats]);

  const rangeLabel = useMemo(() => {
    const f = appliedFrom || "-";
    const t = appliedTo || "-";
    return `${f} → ${t}`;
  }, [appliedFrom, appliedTo]);

  // Preset → doğrudan uygulanır (canlı)
  const applyPreset = (preset) => {
    const now = new Date();

    if (preset === "all") {
      setAppliedFrom("");
      setAppliedTo("");
      return;
    }
    if (preset === "7d") {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      setAppliedFrom(ymd(from));
      setAppliedTo(ymd(now));
      return;
    }
    if (preset === "30d") {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      setAppliedFrom(ymd(from));
      setAppliedTo(ymd(now));
      return;
    }
    if (preset === "thisMonth") {
      setAppliedFrom(ymd(startOfMonth(now)));
      setAppliedTo(ymd(endOfMonth(now)));
      return;
    }
    if (preset === "prevMonth") {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setAppliedFrom(ymd(startOfMonth(prev)));
      setAppliedTo(ymd(endOfMonth(prev)));
    }
  };

  const dateActiveCount = (appliedFrom ? 1 : 0) + (appliedTo ? 1 : 0);

  const onClearFilters = () => {
    setAppliedFrom("");
    setAppliedTo("");
  };

  const infoContent = (
    <ul>
      <li>
        <b>Filtreler</b> ile rapor <b>tarih aralığını</b> daraltın; hazır
        aralıklardan (Son 7/30 gün, Bu ay, Geçen ay, Tümü) seçebilir veya
        başlangıç–bitiş tarihini elle girebilirsiniz. Değişiklik anında uygulanır.
      </li>
      <li>
        <b>Zaman Serisi</b> bölümünde <b>Gün / Hafta / Ay</b> kırılımını seçin;
        ok tuşuyla <b>eski→yeni / yeni→eski</b> sıralayın.
      </li>
      <li>
        <b>Dışa Aktar</b> ile seçili aralık ve kırılıma göre <b>CSV</b> indirin.
      </li>
    </ul>
  );

  const onDownloadCsv = useCallback(async () => {
    if (!surveyId) return;

    try {
      const res = await downloadSurveyReportCsv(surveyId, {
        from: appliedFrom || undefined,
        to: appliedTo || undefined,
        bucket,
      });

      const blob =
        res?.data instanceof Blob
          ? res.data
          : new Blob([res.data], { type: "text/csv;charset=utf-8" });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `anket_${surveyId}_rapor.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("CSV indirilemedi. Console ve Network tabını kontrol edin.");
    }
  }, [surveyId, appliedFrom, appliedTo, bucket]);

  return (
    <div className="applications-page survey-report-detail-page">
      {/* STICKY: HEADER + TOOLBAR — paylaşılan PageHeader */}
      <PageHeader
        title={title}
        infoTitle="Anket Raporu — Bilgi"
        infoContent={infoContent}
        tabs={
          <span className="srd-range-badge" title="Uygulanan tarih aralığı">
            <ClockIcon className="srd-range-ico" />
            <span>{rangeLabel}</span>
          </span>
        }
      >
        <FilterSheet
          activeCount={dateActiveCount}
          onClear={onClearFilters}
          title="Filtreler"
          label="Filtreler"
        >
          {/* Hazır aralıklar (çip) */}
          <div className="afp-section">
            <div className="afp-label">Hazır aralıklar</div>
            <div className="srd-presets">
              <button type="button" className="srd-pillbtn" onClick={() => applyPreset("7d")}>
                Son 7 gün
              </button>
              <button type="button" className="srd-pillbtn" onClick={() => applyPreset("30d")}>
                Son 30 gün
              </button>
              <button type="button" className="srd-pillbtn" onClick={() => applyPreset("thisMonth")}>
                Bu ay
              </button>
              <button type="button" className="srd-pillbtn" onClick={() => applyPreset("prevMonth")}>
                Geçen ay
              </button>
              <button type="button" className="srd-pillbtn" onClick={() => applyPreset("all")}>
                Tümü
              </button>
            </div>
          </div>

          {/* Tarih aralığı (canlı) */}
          <div className="afp-section">
            <div className="afp-label">Tarih aralığı</div>
            <div className="afp-dates">
              <input
                className="md-input"
                type="date"
                value={appliedFrom}
                onChange={(e) => setAppliedFrom(e.target.value)}
              />
              <span className="afp-dates-sep">–</span>
              <input
                className="md-input"
                type="date"
                value={appliedTo}
                onChange={(e) => setAppliedTo(e.target.value)}
              />
            </div>
          </div>
        </FilterSheet>

        <button
          type="button"
          className="srd-export-btn"
          onClick={onDownloadCsv}
          title="Dışa aktar (CSV)"
        >
          <ArrowDownTrayIcon className="srd-export-ico" />
          <span>Dışa Aktar</span>
        </button>
      </PageHeader>

      {/* SUMMARY */}
        <div className="srd-surface">
          <div className="srd-section-title">Özet</div>

          {statsLoading ? (
            <div className="srd-state">Yükleniyor...</div>
          ) : statsErrorText ? (
            <div className="srd-state srd-state-error">{statsErrorText}</div>
          ) : stats?.totals ? (
            <div className="srd-metrics">
              <div className="srd-metric">
                <div className="srd-metric-k">Toplam Yanıt</div>
                <div className="srd-metric-v">{stats.totals.responses ?? 0}</div>
              </div>

              
              <div className="srd-metric">
                <div className="srd-metric-k">Soru</div>
                <div className="srd-metric-v">{stats?.questions?.length ?? 0}</div>
              </div>
            </div>
          ) : (
            <div className="srd-state">Veri yok.</div>
          )}
        </div>

        {/* TIMESERIES */}
        <div className="srd-surface">
          <div className="srd-times-head">
            <div className="srd-section-title">Zaman Serisi</div>

            <div className="srd-times-actions">
              <div className="srd-seg">
                <button
                  type="button"
                  className={`srd-seg-btn ${bucket === "day" ? "is-active" : ""}`}
                  onClick={() => setBucket("day")}
                  disabled={timeseriesLoading}
                >
                  Gün
                </button>
                <button
                  type="button"
                  className={`srd-seg-btn ${bucket === "week" ? "is-active" : ""}`}
                  onClick={() => setBucket("week")}
                  disabled={timeseriesLoading}
                >
                  Hafta
                </button>
                <button
                  type="button"
                  className={`srd-seg-btn ${bucket === "month" ? "is-active" : ""}`}
                  onClick={() => setBucket("month")}
                  disabled={timeseriesLoading}
                >
                  Ay
                </button>
              </div>

              <button
                type="button"
                className="srd-iconbtn srd-iconbtn-ghost"
                onClick={() => setSeriesOrder((v) => (v === "asc" ? "desc" : "asc"))}
                title="Eski/Yeni sıralama"
                disabled={timeseriesLoading}
              >
                <ArrowsUpDownIcon className="srd-iconbtn-ico" />
                <span className="srd-iconbtn-text">
                  {seriesOrder === "asc" ? "Eski → Yeni" : "Yeni → Eski"}
                </span>
              </button>
            </div>
          </div>

          {timeseriesLoading ? (
            <div className="srd-state">Yükleniyor...</div>
          ) : timeseriesErrorText ? (
            <div className="srd-state srd-state-error">{timeseriesErrorText}</div>
          ) : displaySeries.length ? (
            <div className="srd-times-list">
              {displaySeries.map((r, idx) => {
                const val = Number(r?.responses || 0);
                const w = Math.max(2, Math.round((val * 100) / maxSeriesResponses));
                return (
                  <div className="srd-times-row" key={`${r.date}-${idx}`}>
                    <div className="srd-times-date">{r.date}</div>

                    <div className="srd-times-right">
                      <div className="srd-mini-track" aria-hidden="true">
                        <div className="srd-mini-fill" style={{ width: `${w}%` }} />
                      </div>
                      <div className="srd-times-num">{val}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="srd-state">Veri yok.</div>
          )}
        </div>

        {/* QUESTIONS */}
        <div className="srd-surface">
          <div className="srd-section-title">Sorular ve Cevaplar</div>

          {statsLoading ? (
            <div className="srd-state">Yükleniyor...</div>
          ) : statsErrorText ? (
            <div className="srd-state srd-state-error">{statsErrorText}</div>
          ) : stats?.questions?.length ? (
            <div className="srd-q-list">
              {stats.questions.map((q) => {
                const total = Number(q.total_answers || 0);

                return (
                  <div key={q.id} className="srd-q-card">
                    <div className="srd-q-head">
                      <div className="srd-q-left">
                        <div className="srd-q-title">
                          <span className="srd-q-no">{q.order}.</span>
                          <span className="srd-q-text">{q.text}</span>
                        </div>
                        <div className="srd-q-sub">
                          Toplam: <b>{total}</b> yanıt
                        </div>
                      </div>
                    </div>

                    {q.choices?.length ? (
                      <div className="srd-answers-box">
                        {q.choices.map((c) => {
                          const count = Number(c.count || 0);
                          const pct = Number(c.percent || 0);
                          const w = total > 0 ? Math.min(100, Math.max(0, pct)) : 0;

                          return (
                            <div key={c.id} className="srd-answer-row">
                              <div className="srd-answer-left">
                                <span className="srd-bullet" aria-hidden="true">
                                  •
                                </span>
                                <span className="srd-answer-text">{c.text}</span>
                              </div>

                              <div className="srd-answer-right">
                                <span className="srd-answer-count">{count}</span>

                                <div className="srd-mini-track srd-mini-track-choice" aria-hidden="true">
                                  <div className="srd-mini-fill" style={{ width: `${w}%` }} />
                                </div>

                                <span className="srd-answer-pct">%{pct}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="srd-state">Seçenek yok.</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="srd-state">Veri yok.</div>
          )}
        </div>
    </div>
  );
}
